import { useState } from "react";
import {
  CheckIcon,
  CircleCheckIcon,
  CircleDashedIcon,
  EyeOffIcon,
  ListChecksIcon,
  Minimize2Icon,
} from "lucide-react";

import { Center, Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";

import { SEARCH_V2_ANALYTICS_METADATA } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";

const SEARCH_CHECKLIST_ITEMS = [
  {
    details: [
      "Start with 2–3, then add more.",
      'Replace compound keywords (e.g. "CNC Lathe") with separate keywords ("CNC" and "Lathe").',
    ],
    key: "keywords",
    pillLabel: "Keywords",
    title: "Add or remove Keywords",
  },
  {
    description: "Explore filters like title, pay, rejections, etc.",
    key: "filters",
    pillLabel: "Filters",
    title: "Adjust your filters",
  },
  {
    description: "Exact for hard requirements, broad for adjacent experience.",
    key: "tuning",
    pillLabel: "Tuning",
    title: "Tune results",
  },
] as const;

export const SearchV2ChecklistPanel = ({
  completedItems,
  longestHiddenDays,
  onClearHiddenUsers,
  onItemToggle,
  staleHiddenUserCount,
}: {
  completedItems: string[];
  longestHiddenDays: number;
  onClearHiddenUsers: () => void;
  onItemToggle: (item: string) => void;
  staleHiddenUserCount: number;
}) => {
  /* React Hooks */
  const [isCollapsed, setIsCollapsed] = useState(true);

  /* Render */
  // Shown in both collapsed and expanded states — hidden candidates that have
  // gone stale are easy to forget behind the filters dialog.
  const renderStaleHiddenButton = () => {
    if (staleHiddenUserCount === 0) {
      return null;
    }
    return (
      <Row
        className="group/unhide cursor-pointer items-center justify-between overflow-hidden rounded-sm border border-amber-200 bg-amber-50 pl-2.5 text-xs font-medium text-amber-800 hover:bg-amber-100 hover:text-amber-900"
        onClick={(event) => {
          // Collapsed panel expands on click — unhiding shouldn't also expand.
          event.stopPropagation();
          onClearHiddenUsers();
        }}
        title="Unhide these candidates so they show in search results again"
      >
        <Text className="text-xs font-medium text-amber-800 group-hover/unhide:text-amber-900">
          {staleHiddenUserCount} hidden for {longestHiddenDays}d
        </Text>
        <Row className="h-8 items-center gap-2 border-l border-amber-200 px-3 text-xs font-medium group-hover/unhide:bg-amber-200/60">
          <EyeOffIcon className="h-3.5 w-3.5 shrink-0 text-amber-600" />
          Unhide
        </Row>
      </Row>
    );
  };

  const render = () => {
    if (isCollapsed) {
      return (
        <Column
          className="xl:w-70 hidden w-60 cursor-pointer gap-3 rounded-xl border border-slate-100 bg-slate-50 p-4 shadow-sm hover:bg-slate-100 xl:flex 2xl:w-80"
          onClick={() => {
            mixpanelUtils.trackButtonClick(
              "Expand search checklist",
              MIXPANEL_SCREENS.JOB,
              SEARCH_V2_ANALYTICS_METADATA,
            );
            setIsCollapsed(false);
          }}
          title="Expand search checklist"
        >
          <Row className="items-center justify-between">
            <Text className="font-plex-mono text-[11px] font-medium uppercase tracking-wider text-slate-400">
              Checklist
            </Text>
            <ListChecksIcon className="h-3.5 w-3.5 text-blue-700" />
          </Row>
          <Row className="justify-between gap-3">
            {SEARCH_CHECKLIST_ITEMS.map((item) => {
              const isCompleted = completedItems.includes(item.key);
              return (
                <Row
                  className="items-center gap-1.5"
                  key={item.key}
                  title={item.title}
                >
                  {isCompleted ? (
                    <CircleCheckIcon className="h-3.5 w-3.5 text-blue-600" />
                  ) : (
                    <CircleDashedIcon className="h-3.5 w-3.5 text-slate-300" />
                  )}
                  <Text
                    className={cn(
                      "text-xs font-normal",
                      isCompleted ? "text-slate-700" : "text-slate-400",
                    )}
                  >
                    {item.pillLabel}
                  </Text>
                </Row>
              );
            })}
          </Row>
          {renderStaleHiddenButton()}
        </Column>
      );
    }

    return (
      <Column className="group/checklist xl:w-70 hidden w-60 rounded-xl border border-slate-100 bg-slate-50 p-4 shadow-sm xl:flex 2xl:w-80">
        <Column className="shrink-0 gap-3">
          <Row className="items-center justify-between">
            <Text className="font-plex-mono text-[11px] font-medium uppercase tracking-wider text-slate-400">
              Search checklist
            </Text>
            <Row className="relative h-6 w-6 shrink-0 items-center justify-center">
              <ListChecksIcon className="h-3.5 w-3.5 text-blue-700 transition-opacity group-hover/checklist:opacity-0" />
              <Button
                className="absolute inset-0 h-6 w-6 opacity-0 transition-opacity group-hover/checklist:opacity-100"
                onClick={() => {
                  setIsCollapsed(true);
                }}
                size="icon"
                title="Minimize search checklist"
                type="button"
                variant="ghost"
              >
                <Minimize2Icon className="h-3.5 w-3.5 text-slate-500" />
              </Button>
            </Row>
          </Row>
          <Text className="text-xs font-normal text-slate-500">
            Try these tips to improve your search results.
          </Text>
        </Column>
        <Column className="mt-3 gap-1">
          {SEARCH_CHECKLIST_ITEMS.map((item) => {
            const isCompleted = completedItems.includes(item.key);
            return (
              <Button
                className="h-auto w-full items-start justify-start gap-3 whitespace-normal px-3 py-2.5 text-left hover:bg-slate-100"
                key={item.key}
                onClick={() => {
                  onItemToggle(item.key);
                }}
                type="button"
                variant="ghost"
              >
                <Center
                  className={cn(
                    "h-5 w-5 shrink-0 rounded-full border border-slate-200 bg-slate-50",
                    isCompleted && "border-blue-600 bg-blue-600 text-white",
                  )}
                >
                  {isCompleted ? <CheckIcon className="h-3 w-3" /> : null}
                </Center>
                <Column className="min-w-0 gap-1">
                  <Text
                    className={cn(
                      "text-sm font-normal text-slate-700",
                      isCompleted && "text-slate-400 line-through",
                    )}
                  >
                    {item.title}
                  </Text>
                  {"description" in item ? (
                    <Text className="whitespace-normal text-xs font-normal leading-relaxed text-slate-400">
                      {item.description}
                    </Text>
                  ) : null}
                  {"details" in item ? (
                    <Column className="gap-1">
                      {item.details.map((detail) => (
                        <Text
                          className="whitespace-normal text-xs font-normal leading-relaxed text-slate-400"
                          key={detail}
                        >
                          {detail}
                        </Text>
                      ))}
                    </Column>
                  ) : null}
                </Column>
              </Button>
            );
          })}
        </Column>
        {staleHiddenUserCount > 0 ? (
          <Column className="mt-3">{renderStaleHiddenButton()}</Column>
        ) : null}
      </Column>
    );
  };

  return render();
};
